import { useState, useCallback } from 'react';

export type AngelVariant =
  | 'gold'
  | 'green'
  | 'blue'
  | 'purple'
  | 'white'
  | 'pink'
  | 'default';

const variantClasses: Record<AngelVariant, string> = {
  gold: 'text-amber-400',
  green: 'text-emerald-400',
  blue: 'text-sky-400',
  purple: 'text-violet-400',
  white: 'text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.15)]',
  pink: 'text-rose-300',
  default: 'text-foreground-500',
};

interface AngelIconProps {
  variant?: AngelVariant;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeMap = {
  sm: { container: 'w-7 h-7', heart: 'text-base', sparkle: 'text-[7px]' },
  md: { container: 'w-9 h-9', heart: 'text-xl', sparkle: 'text-[9px]' },
  lg: { container: 'w-11 h-11', heart: 'text-2xl', sparkle: 'text-[11px]' },
};

export default function AngelIcon({ variant = 'default', size = 'md', className = '' }: AngelIconProps) {
  const [glow, setGlow] = useState(false);
  const colorClass = variantClasses[variant];
  const s = sizeMap[size];

  const handleInteract = useCallback(() => {
    setGlow(true);
    setTimeout(() => setGlow(false), 600);
  }, []);

  return (
    <div
      className={`relative inline-flex items-center justify-center ${s.container} select-none ${className}`}
      onMouseEnter={handleInteract}
      onClick={handleInteract}
      role="img"
      aria-label="AI Angel"
    >
      {/* Halo */}
      <div
        className={`absolute -top-0.5 left-1/2 -translate-x-1/2 w-3 h-1.5 rounded-full border border-current opacity-40 transition-opacity duration-500 ${colorClass} ${glow ? 'opacity-70' : ''}`}
      />
      {/* Left wing sparkle */}
      <i
        className={`ri-sparkling-fill absolute -left-0.5 top-1/2 -translate-y-1/2 ${s.sparkle} opacity-30 -rotate-12 transition-opacity duration-500 ${colorClass} ${glow ? 'opacity-60' : ''}`}
      />
      {/* Right wing sparkle */}
      <i
        className={`ri-sparkling-fill absolute -right-0.5 top-1/2 -translate-y-1/2 ${s.sparkle} opacity-30 rotate-12 transition-opacity duration-500 ${colorClass} ${glow ? 'opacity-60' : ''}`}
      />
      {/* Heart body */}
      <i
        className={`ri-heart-3-fill ${s.heart} ${colorClass} transition-transform duration-300 ${glow ? 'scale-110' : 'scale-100'}`}
      />
    </div>
  );
}